import type {
  MarketApps,
  MarketAppWithTabs,
  StaticDataChartWidgetV2,
  TilesWidgetV2
} from '@/features/dashboard';
import { http } from '@/services/http';
import { getInternalBasePath } from '@/shared/lib/environmentPaths';
import { sanitizeName } from '@/shared/lib/format/sanitizeName';
import { parseMarketComponent } from '../lib/parseMarketComponent';
import type { BaseTabType, Market, MarketDto, MarketsPageType } from '../types';

import type { MarketTabWidgets, WidgetItem } from './markets.types';

type MarketWidgetDefinition = Record<string, unknown> & {
  layout?: unknown;
  tabs?: MarketWidgetDefinitionTab[];
};

type MarketWidgetDefinitionTab = Record<string, unknown> & {
  name?: string;
  type?: string;
};

type MarketWidgetsResponseTab = {
  name: string;
  widgets: Array<{
    type: string;
    definition: MarketWidgetDefinition;
  }>;
};

type MarketPreviewTemplateTab = MarketAppWithTabs['tabs'][number];

const STATIC_DATA_CHART_PREVIEW_TEST_COMPONENT_ID = 'static-data-chart-preview-test-widget';

const shouldInjectStaticDataChartPreviewTestWidget = () => import.meta.env.DEV;

const hasStaticDataChart = (template: MarketApps): boolean =>
  template.some(
    app =>
      'tabs' in app &&
      app.tabs.some(tab => tab.type === 'graph' && tab.metadata?.graphType === 'static')
  );

const isStaticDataChartTemplateTab = (
  tab: MarketPreviewTemplateTab | undefined
): tab is StaticDataChartWidgetV2 => {
  if (tab?.type !== 'graph' || tab.metadata?.graphType !== 'static') {
    return false;
  }

  return 'chartConfig' in tab;
};

const restorePreviewTabFromTemplate = (
  tab: BaseTabType,
  templateTab: MarketPreviewTemplateTab | undefined
): BaseTabType => {
  const tabWithTemplateId = {
    ...tab,
    id: templateTab?.id ?? tab.id
  };

  if (!isStaticDataChartTemplateTab(templateTab)) {
    return tabWithTemplateId;
  }

  return {
    ...tabWithTemplateId,
    name: templateTab.name ?? tabWithTemplateId.name,
    type: 'graph',
    metadata: {
      ...tabWithTemplateId.metadata,
      ...templateTab.metadata
    },
    tool_call_id: templateTab.tool_call_id,
    chartConfig: templateTab.chartConfig
  };
};

const withStaticDataChartPreviewTestWidget = (
  page: MarketsPageType,
  template: MarketApps
): MarketsPageType => {
  if (
    !shouldInjectStaticDataChartPreviewTestWidget() ||
    hasStaticDataChart(template) ||
    page.components.some(component => component.id === STATIC_DATA_CHART_PREVIEW_TEST_COMPONENT_ID)
  ) {
    return page;
  }

  return {
    ...page,
    components: [...page.components]
  };
};

export const marketsApi = {
  getMarket: async (marketId: string): Promise<Market> => {
    const { data } = await http.internal.get<MarketDto>(
      `${getInternalBasePath()}markets/${marketId}`
    );

    const pages = data.pages.map(page => ({
      ...page,
      id: sanitizeName(page.name),
      components: page.components.map(component => {
        return parseMarketComponent(component, data.version);
      })
    }));

    return {
      ...data,
      id: marketId,
      pages
    };
  },

  getMarketWidgets: async (marketId: string): Promise<MarketTabWidgets[]> => {
    const { data } = await http.internal.get<MarketWidgetsResponseTab[]>(
      `${getInternalBasePath()}markets/${marketId}/widgets`
    );

    return data.map(tab => {
      const widgets: WidgetItem[] = [];
      for (const widget of tab.widgets) {
        if (widget.definition.tabs) {
          widget.definition.tabs.forEach(widgetTab => {
            const widgetType =
              widget.type === 'tabcontainer' && widgetTab.type ? widgetTab.type : widget.type;
            widgets.push({
              name: widgetTab.name,
              type: widgetType,
              definition: { ...widgetTab, layout: widget.definition.layout, type: widgetType }
            });
          });
        } else {
          widgets.push(widget);
        }
      }
      return { name: tab.name, widgets };
    });
  },

  previewMarket: async (template: MarketApps): Promise<MarketsPageType | null> => {
    const { data } = await http.internal.post<MarketDto>(
      `${getInternalBasePath()}markets/preview`,
      {
        version: 2,
        pages: [
          {
            name: '',
            components: template
          }
        ]
      }
    );

    const pages: MarketsPageType[] = data.pages.map(page => ({
      ...page,
      id: sanitizeName(page.name),
      components: page.components.map((component, componentIndex) => {
        const parsedComponent = parseMarketComponent(component, '2');
        if (parsedComponent.component?.type === 'tiles') {
          const tilesApp = template[0] as TilesWidgetV2 | undefined;
          return {
            ...parsedComponent,
            component: {
              ...parsedComponent.component,
              tiles: parsedComponent.component.tiles.map((tile, index) => ({
                ...tile,
                id: tilesApp?.tiles[index]?.id ?? tile.id
              }))
            }
          };
        }
        const tabbedApp = template[componentIndex] as Partial<MarketAppWithTabs> | undefined;
        return {
          ...parsedComponent,
          id: tabbedApp?.id ?? parsedComponent.id,
          tabs: parsedComponent.tabs?.map((tab, index) =>
            restorePreviewTabFromTemplate(tab, tabbedApp?.tabs?.[index])
          )
        };
      })
    }));

    if (pages.length === 0) {
      return null;
    }

    return withStaticDataChartPreviewTestWidget(pages[0], template);
  }
};
